import {ReactNode} from 'react';

import {
  FeatureFlags,
  FeatureFlagsProvider,
  useWidgetTriggerParams,
} from '@stripe-internal/conversations-ui';
import {ViewMode} from '@stripe-internal/support-conversations-controller';

import {
  isDotComAudience,
  isDotComEntryPoint,
} from '../../core/lib/util/conversationUtils';
import {isDotComCsfSelfServeRedirectTriggerPoint} from '../../core/lib/util/triggerPoints';
import {
  useAudience,
  useEntryPoint,
  useFlags,
  useViewMode,
} from '../../core/hooks/appHooks';

const conversationsUIViewModeByAppViewMode = {
  [ViewMode.SupportWidget]: 'support-widget',
  [ViewMode.CSATSurvey]: 'csat-survey',
  [ViewMode.Drawer]: 'drawer',
  [ViewMode.Minimal]: 'minimal',
} satisfies Record<ViewMode, FeatureFlags['viewMode']>;

export function ConversationsUIFlagsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const featureFlags = useFlags();
  const audience = useAudience();
  const entryPoint = useEntryPoint();
  const {triggerParams} = useWidgetTriggerParams();
  const {
    enableUniversalHelp,
    universalHelpUseNewDesign,
    universalHelpDotComFeedbackButtons,
    universalHelpDotComNoLlm,
    universalHelpDotComCsf,
    universalHelpMessagesTopAnchored,
    universalHelpUseInlineRacEmailForm,
    consolidatedConversationsLiveAnswersRow,
    universalHelpEncourageMoreDetailFromUserMse5042,
    enableScreenshareUi,
    enableScreenshareContextProvider,
    useExtractedConversationsAssistant,
    enableUhPendingInput,
  } = featureFlags;
  const viewMode = useViewMode();

  const value: FeatureFlags = {
    newUniversalHelpDesign: enableUniversalHelp && universalHelpUseNewDesign,
    universalHelpDotComDesign:
      enableUniversalHelp &&
      (audience === 'dot_com' || isDotComEntryPoint(entryPoint)),
    dotComFeedbackButtons:
      enableUniversalHelp && universalHelpDotComFeedbackButtons,
    dotComNoLlm: enableUniversalHelp && universalHelpDotComNoLlm,
    dotComCsf: enableUniversalHelp && universalHelpDotComCsf,
    dotComCsfSelfServe: isDotComCsfSelfServeRedirectTriggerPoint(
      triggerParams?.triggerPoint,
    ),
    messagesTopAnchored:
      enableUniversalHelp && universalHelpMessagesTopAnchored,
    inlineRacEmailForm:
      enableUniversalHelp && universalHelpUseInlineRacEmailForm,
    conversationsLiveAnswersRow:
      consolidatedConversationsLiveAnswersRow ||
      isDotComAudience(audience, entryPoint) ||
      useExtractedConversationsAssistant,
    universalHelpEncourageMoreDetailFromUserMse5042,
    enableScreenshareUi,
    enableScreenshareContextProvider,
    enableUhPendingInput,
    viewMode: conversationsUIViewModeByAppViewMode[viewMode],
  };

  return <FeatureFlagsProvider value={value}>{children}</FeatureFlagsProvider>;
}
